import React, { useState, useEffect, useMemo } from 'react';
import { getTermineByWeek, getTeamsByOrganization, getUsersByOrganization, getOrganizations, addTermin, deleteTermin, addLead } from '../api';
import AddTerminDialog from '../components/termine/AddTerminDialog';
import AddBlockDialog from '../components/termine/AddBlockDialog';
import TerminCalendar from '../components/termine/TerminCalendar';
import FachberaterTerminCalendar from '../components/termine/FachberaterTerminCalendar';
import TerminDetailDialog from '../components/termine/TerminDetailDialog';
import MultiAssignModal from '../components/termine/MultiAssignModal';

function Termine({ user }) {
  const [termine, setTermine] = useState([]);
  const [teams, setTeams] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [selectedBerater, setSelectedBerater] = useState('');
  const [berater, setBerater] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTermin, setSelectedTermin] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [nameSearch, setNameSearch] = useState('');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showMultiAssignModal, setShowMultiAssignModal] = useState(false);

  // Rollenprüfungen
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;
  const isFachberater = user?.isFachberater || false;
  const isOrgLeader = user?.isOrgLeader || false;

  useEffect(() => {
    if (isCallcenterLeader || isCallcenterMitarbeiter || isFachberater || isOrgLeader) {
      loadInitialData();
    }
  }, [user.id, isCallcenterLeader, isCallcenterMitarbeiter, isFachberater, isOrgLeader]);

  useEffect(() => {
    if ((isCallcenterLeader || isCallcenterMitarbeiter) && selectedTeam) {
      loadBerater();
    }
  }, [selectedTeam]);

  useEffect(() => {
    setSelectedBerater('');
  }, [selectedTeam]);

  useEffect(() => {
    if (isCallcenterLeader || isCallcenterMitarbeiter || isFachberater || isOrgLeader) {
      loadTermine();
    }
  }, [selectedTeam, selectedOrganization, currentDate]);

  const loadInitialData = async () => {
    try {
      // Organisationen laden (für Callcenter Leader, Callcenter Mitarbeiter und Orgleiter)
      if (isCallcenterLeader || isCallcenterMitarbeiter || isOrgLeader) {
        const orgsResponse = await getOrganizations(user.id);
        if (orgsResponse.success) {
          setOrganizations(orgsResponse.organizations || []);
        }

        // Alle Teams laden (für Callcenter Leader, Callcenter Mitarbeiter und Orgleiter)
        const teamsResponse = await getTeamsByOrganization(user.id, user.organization_id);
        if (teamsResponse.success) {
          setTeams(teamsResponse.teams || []);
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten: ' + err.message);
    }
  };

  const loadBerater = async () => {
    try {
      const response = await getUsersByOrganization(user.id, user.organization_id);
      if (response.success) {
        // Nur Fachberater (role_type 6) filtern und nach Team filtern
        const filteredBerater = response.users.filter(user =>
          user.role_type === 6 &&
          (!selectedTeam || user.team_id?.toString() === selectedTeam.toString())
        );
        setBerater(filteredBerater);
      }
    } catch (err) {
      setError('Fehler beim Laden der Berater: ' + err.message);
    }
  };

  const loadTermine = async () => {
    setLoading(true);
    setError('');

    try {
      // Berechne den Wochenstart für die aktuelle Woche
      const weekStart = getWeekStart(currentDate);

      let response;

      if (isFachberater) {
        // Fachberater sehen nur ihre eigenen Termine für die aktuelle Woche
        response = await getTermineByWeek(user.id, weekStart, {
          // Keine zusätzlichen Filter für Fachberater
        });

        if (response && response.success && response.termine) {
          let filteredTermine = response.termine.filter(termin =>
            termin.mitarbeiter_id === user.id
          );

          // Namenssuche für Fachberater
          if (nameSearch.trim()) {
            const searchTerm = nameSearch.toLowerCase().trim();
            filteredTermine = filteredTermine.filter(termin => {
              const kundeName = (termin.kunde_name || '').toLowerCase();
              const mitarbeiterName = (termin.mitarbeiter_name || '').toLowerCase();
              return kundeName.includes(searchTerm) || mitarbeiterName.includes(searchTerm);
            });
          }

          response.termine = filteredTermine;
        }
      } else if (isCallcenterLeader || isCallcenterMitarbeiter) {
        // Callcenter-Rollen verwenden wochenbasierte API
        response = await getTermineByWeek(user.id, weekStart, {
          teamId: selectedTeam,
          organizationId: selectedOrganization
        });

        if (response && response.success && response.termine) {
          let filteredTermine = response.termine;

          // Nach Team filtern
          if (selectedTeam) {
            filteredTermine = filteredTermine.filter(termin =>
              termin.team_id === parseInt(selectedTeam)
            );
          }

          // Nach Berater filtern
          if (selectedBerater) {
            filteredTermine = filteredTermine.filter(termin =>
              termin.mitarbeiter_id === parseInt(selectedBerater)
            );
          }

          // Namenssuche für Callcenter
          if (nameSearch.trim()) {
            const searchTerm = nameSearch.toLowerCase().trim();
            filteredTermine = filteredTermine.filter(termin => {
              const kundeName = (termin.kunde_name || '').toLowerCase();
              const mitarbeiterName = (termin.mitarbeiter_name || '').toLowerCase();
              return kundeName.includes(searchTerm) || mitarbeiterName.includes(searchTerm);
            });
          }

          response.termine = filteredTermine;
        }
      } else if (isOrgLeader) {
        // Orgleiter verwenden wochenbasierte API
        response = await getTermineByWeek(user.id, weekStart, {
          organizationId: user.organization_id
        });

        if (response && response.success && response.termine) {
          let filteredTermine = response.termine;

          // Nach Organisation filtern
          if (selectedOrganization) {
            filteredTermine = filteredTermine.filter(termin =>
              termin.organization_id === parseInt(selectedOrganization)
            );
          }

          // Nach Team filtern
          if (selectedTeam) {
            filteredTermine = filteredTermine.filter(termin =>
              termin.team_id === parseInt(selectedTeam)
            );
          }

          // Namenssuche für OrgLeader
          if (nameSearch.trim()) {
            const searchTerm = nameSearch.toLowerCase().trim();
            filteredTermine = filteredTermine.filter(termin => {
              const kundeName = (termin.kunde_name || '').toLowerCase();
              const mitarbeiterName = (termin.mitarbeiter_name || '').toLowerCase();
              return kundeName.includes(searchTerm) || mitarbeiterName.includes(searchTerm);
            });
          }

          response.termine = filteredTermine;
        }
      }

      if (!response) {
        setError('Fehler: Keine Antwort vom Server erhalten.');
      } else if (response.success) {
        setTermine(response.termine || []);
      } else {
        setError(response.error || 'Fehler beim Laden der Termine');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Hilfsfunktion zum Berechnen des Wochenstarts (Montag)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Montag als erster Tag
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  // Termin-Statistik für die aktuelle Woche: Gesamtanzahl + pro Wochentag
  const weekSummary = useMemo(() => {
    const weekDayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    const dayCounts = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const count = (termine || []).filter((t) => {
        const tDate = (t.datum || '').split('T')[0].split(' ')[0];
        return tDate === dateStr;
      }).length;
      dayCounts.push({ day: weekDayNames[i], count });
    }
    const total = (termine || []).length;
    return { total, dayCounts };
  }, [termine, currentDate]);

  // Füge useEffect für die Berater-Filterung hinzu
  useEffect(() => {
    // Termine neu laden, wenn sich der Berater-Filter ändert (auch wenn auf "Alle Berater" zurückgesetzt)
    loadTermine();
  }, [selectedBerater]);

  // Füge useEffect für die Team-Filterung hinzu
  useEffect(() => {
    // Termine neu laden, wenn sich der Team-Filter ändert (auch wenn auf "Alle Teams" zurückgesetzt)
    loadTermine();
  }, [selectedTeam]);

  // Füge useEffect für die Organisations-Filterung hinzu
  useEffect(() => {
    // Termine neu laden, wenn sich der Organisations-Filter ändert
    loadTermine();
  }, [selectedOrganization]);

  // Füge useEffect für die Namenssuche hinzu
  useEffect(() => {
    // Termine neu laden, wenn sich die Namenssuche ändert
    loadTermine();
  }, [nameSearch]);

  // Teams für ausgewählte Organisation laden
  const loadTeamsForOrganization = async (orgId) => {
    try {
      if (!orgId) {
        // Wenn keine Organisation ausgewählt ist, alle Teams laden
        const teamsResponse = await getTeamsByOrganization(user.id, user.organization_id);
        if (teamsResponse.success) {
          setTeams(teamsResponse.teams || []);
        }
      } else {
        // Teams für spezifische Organisation laden
        const teamsResponse = await getTeamsByOrganization(user.id, orgId);
        if (teamsResponse.success) {
          setTeams(teamsResponse.teams || []);
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Teams: ' + err.message);
    }
  };

  // Teams neu laden, wenn sich die ausgewählte Organisation ändert
  useEffect(() => {
    loadTeamsForOrganization(selectedOrganization);
    setSelectedTeam(''); // Team zurücksetzen, wenn Organisation geändert wird
  }, [selectedOrganization]);

  const handleAddTermin = (date, timeSlot = null) => {
    // Nur Callcenter Leader können Termine erstellen
    if (!isCallcenterLeader) return;

    setSelectedDate(date);
    if (timeSlot) {
      setSelectedTimeSlot(timeSlot);
    }
    setShowAddDialog(true);
  };

  const handleTerminClick = (terminId) => {
    const termin = termine.find(t => t.id === terminId);
    if (termin) {
      setSelectedTermin(termin);
      setShowDetailDialog(true);
    }
  };

  const handleTerminDeleted = (deletedTerminId) => {
    // Termin aus der lokalen Liste entfernen
    setTermine(termine.filter(t => t.id !== deletedTerminId));
  };

  const handleSaveTermin = async (terminData) => {
    try {
      console.log("🔍 handleSaveTermin aufgerufen mit:", terminData);

      // API-Aufruf für Termin-Erstellung
      const response = await addTermin(user.id, terminData);

      if (response.success) {
        // Termin zur lokalen Liste hinzufügen
        setTermine(prevTermine => [...prevTermine, response.termin || terminData]);
        setShowAddDialog(false);
        setSelectedDate(null);
        setSelectedTimeSlot(null);
      } else {
        setError(response.error || 'Fehler beim Erstellen des Termins');
      }
    } catch (err) {
      console.error("❌ Exception in handleSaveTermin:", err);
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleAddBlock = (date, timeSlot = null) => {
    setSelectedDate(date);
    setSelectedTimeSlot(timeSlot);
    setShowBlockDialog(true);
  };

  const handleSaveBlock = async (blockData) => {
    try {
      console.log("🔍 handleSaveBlock aufgerufen mit:", blockData);
      console.log("🔍 User ID:", user.id);
      console.log("🔍 User organization_id:", user.organization_id);

      // Block als Termin erstellen (für Kalender-Anzeige)
      const blockTerminData = {
        datum: blockData.datum,
        uhrzeit: blockData.uhrzeit,
        dauer: blockData.dauer,
        kundeId: 731, // Spezieller Kunde für Blöcke (wie in deinem Beispiel)
        notizen: blockData.nachricht || '',
        mitarbeiter_id: user.id,
        mitarbeiter_name: user.full_name || user.email,
        team_id: blockData.teamId,
        team_name: teams.find(t => t.id.toString() === blockData.teamId.toString())?.name || '',
        art: 'Blockierung',
        type: 'block'
      };

      console.log("🔍 Block Termin Data:", blockTerminData);

      // API-Aufruf für Block-Erstellung (verwendet addTermin)
      const response = await addTermin(user.id, blockTerminData);

      if (response.success) {
        // Block zur lokalen Liste hinzufügen
        setTermine(prevTermine => [...prevTermine, response.termin || blockTerminData]);
        setShowBlockDialog(false);
        setSelectedDate(null);
        setSelectedTimeSlot(null);
      } else {
        setError(response.error || 'Fehler beim Erstellen des Blocks');
      }
    } catch (err) {
      console.error("❌ Exception in handleSaveBlock:", err);
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleDeleteTermin = async (terminId) => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich löschen?')) {
      return;
    }

    try {
      const response = await deleteTermin(user.id, terminId);
      if (response.success) {
        setTermine(termine.filter(t => t.id !== terminId));
      } else {
        setError(response.error || 'Fehler beim Löschen des Termins');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleMonthChange = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setSelectedTermin(null);
  };

  // Berechtigung prüfen
  if (!isCallcenterLeader && !isCallcenterMitarbeiter && !isFachberater && !isOrgLeader) {
    return (
      <div className="termine-page">
        <div className="no-access">
          <h2>Keine Berechtigung</h2>
          <p>Nur Callcenter Leader, Callcenter Mitarbeiter, Orgleiter und Fachberater haben Zugriff auf die Terminverwaltung.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="termine-page">
      <div className="termine-header">
        <h2>
          {isFachberater ? 'Meine Termine' : 'Terminverwaltung'}
        </h2>
        <div className="termine-controls">
          {/* Organisationen- und Team-Filter für Callcenter Leader, Callcenter Mitarbeiter und Orgleiter */}
          {(isCallcenterLeader || isCallcenterMitarbeiter || isOrgLeader) && (
            <div className="termine-filters">
              {/* Button für Mehrere Termine Zuweisen - nur für Orgleiter */}
              {isOrgLeader && (
                <div className="filter-group">
                  <label style={{ visibility: 'hidden' }}>Action</label>
                  <button
                    className="assign-multiple-button"
                    onClick={() => setShowMultiAssignModal(true)}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#2463ceff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      height: '35px', // Match select height
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Mehrere Termine Zuweisen
                  </button>
                </div>
              )}

              <div className="filter-group">
                <label>Organisation:</label>
                <select
                  value={selectedOrganization}
                  onChange={(e) => setSelectedOrganization(e.target.value)}
                >
                  <option value="">Alle Organisationen</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Projekt:</label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                >
                  <option value="">Alle Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Namenssuche für alle Rollen */}
          <div className="termine-filters">
            <div className="filter-group">
              <label>Namenssuche:</label>
              <input
                type="text"
                placeholder="Kunde oder Mitarbeiter suchen..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                className="name-search-input"
              />
            </div>
          </div>

          {/* Buttons für Callcenter Leader */}
          {isCallcenterLeader && (
            <div className="termin-buttons">
              <button
                className="add-termin-button"
                onClick={() => handleAddTermin(new Date())}
              >
                + Neuer Termin
              </button>
              <button
                className="add-block-button"
                onClick={() => handleAddBlock(new Date())}
              >
                🚫 Zeit blockieren
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Termin-Statistik: Gesamtanzahl + pro Wochentag (wenn Projekt/Organisation ausgewählt oder Fachberater) */}
      {!loading && (selectedTeam || selectedOrganization || isFachberater) && (
        <div className="termine-week-summary">
          <div className="termine-week-summary-total">
            <span className="termine-week-summary-label">Termine diese Woche:</span>
            <span className="termine-week-summary-total-count">{weekSummary.total}</span>
          </div>
          <div className="termine-week-summary-days">
            {weekSummary.dayCounts.map(({ day, count }) => (
              <div key={day} className="termine-week-summary-day">
                <span className="termine-week-summary-day-name">{day}</span>
                <span className="termine-week-summary-day-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Termine werden geladen...</div>
      ) : (
        <>
          {isFachberater ? (
            <FachberaterTerminCalendar
              termine={termine}
              currentDate={currentDate}
              onTerminClick={handleTerminClick}
              onMonthChange={handleMonthChange}
            />
          ) : (
            <TerminCalendar
              termine={termine}
              currentDate={currentDate}
              onDateClick={handleAddTermin}
              onTerminClick={handleTerminClick}
              onMonthChange={handleMonthChange}
            />
          )}
        </>
      )}

      {/* Add Termin Dialog - nur für Callcenter Leader */}
      {showAddDialog && isCallcenterLeader && (
        <AddTerminDialog
          user={user}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          selectedTeam={selectedTeam}
          teams={teams}
          onSave={handleSaveTermin}
          onCancel={() => {
            setShowAddDialog(false);
            setSelectedDate(null);
            setSelectedTimeSlot(null);
          }}
        />
      )}

      {/* Termin Detail Dialog - für Fachberater */}
      {showDetailDialog && selectedTermin && (
        <TerminDetailDialog
          termin={selectedTermin}
          user={user}
          onClose={handleCloseDetailDialog}
          onTerminDeleted={handleTerminDeleted}
          onNavigateToCustomer={(customerId) => {
            handleCloseDetailDialog();
            // Optional: Navigation zur Kundendetail-Seite
          }}
        />
      )}

      {/* Add Block Dialog - nur für Callcenter Leader */}
      {showBlockDialog && isCallcenterLeader && (
        <AddBlockDialog
          user={user}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          selectedTeam={selectedTeam}
          teams={teams}
          onSave={handleSaveBlock}
          onCancel={() => {
            setShowBlockDialog(false);
            setSelectedDate(null);
            setSelectedTimeSlot(null);
          }}
        />
      )}

      {/* Multi Assign Modal - nur für Orgleiter */}
      {showMultiAssignModal && isOrgLeader && (
        <MultiAssignModal
          user={user}
          onClose={() => setShowMultiAssignModal(false)}
          organizations={organizations}
          teams={teams}
          currentDate={currentDate}
        />
      )}

      <style jsx>{`
        .termine-week-summary {
          display: flex;
          align-items: center;
          gap: 24px;
          padding: 12px 16px;
          background: #f0f4ff;
          border: 1px solid #c7d2fe;
          border-radius: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .termine-week-summary-total {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }

        .termine-week-summary-label {
          font-size: 0.95rem;
          color: #374151;
          font-weight: 500;
        }

        .termine-week-summary-total-count {
          font-size: 1.5rem;
          font-weight: 700;
          color: #4a6de5;
        }

        .termine-week-summary-days {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .termine-week-summary-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 36px;
          padding: 6px 10px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }

        .termine-week-summary-day-name {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
        }

        .termine-week-summary-day-count {
          font-size: 1.1rem;
          font-weight: 700;
          color: #334155;
        }

        @media (max-width: 600px) {
          .termine-week-summary {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .termine-week-summary-days {
            width: 100%;
            justify-content: space-between;
          }
          .termine-week-summary-day {
            min-width: 32px;
            padding: 4px 6px;
          }
        }

        .termine-filters {
          display: flex;
          gap: 15px;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
          min-width: 150px;
        }

        .filter-group label {
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 500;
        }

        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background-color: white;
          min-width: 150px;
          font-size: 0.9rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .filter-group select:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .filter-group select:disabled {
          background-color: #f7fafc;
          cursor: not-allowed;
          color: #a0aec0;
        }

        .filter-group select:disabled:hover {
          border-color: #e2e8f0;
        }

        .name-search-input {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background-color: white;
          min-width: 200px;
          font-size: 0.9rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .name-search-input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .name-search-input::placeholder {
          color: #a0aec0;
          font-style: italic;
        }

        .termin-buttons {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .add-termin-button,
        .add-block-button {
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .add-termin-button {
          background: #4a6de5;
          color: white;
        }

        .add-termin-button:hover {
          background: #3c5ce0;
          transform: translateY(-1px);
        }

        .add-block-button {
          background: #e53e3e;
          color: white;
        }

        .add-block-button:hover {
          background: #c53030;
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .termine-filters {
            flex-direction: column;
            gap: 10px;
            align-items: stretch;
          }

          .filter-group {
            min-width: auto;
          }

          .filter-group select,
          .name-search-input {
            min-width: auto;
            width: 100%;
          }

          .termin-buttons {
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }

          .add-termin-button,
          .add-block-button {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .termine-filters {
            gap: 8px;
          }

          .filter-group label {
            font-size: 0.8rem;
          }

          .filter-group select,
          .name-search-input {
            padding: 6px 10px;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
}

export default Termine;